import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaTrash, FaPlus, FaSave, FaSearch } from 'react-icons/fa';
import Swal from 'sweetalert2';

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import { fetchFieldOfficer } from '../../store/features/fieldOfficerSlice';
import { fetchPartners } from '../../store/features/partnersSlice';
import { getRole } from '../../utils/role';

const SOIL_PARAMS = [
  { id: 'ph', label: 'pH' }, { id: 'ec', label: 'EC' }, { id: 'oc', label: 'OC' },
  { id: 'nitrate', label: 'Nitrate' }, { id: 'ammonia', label: 'Ammonia' }, 
  { id: 'phosphorus', label: 'Phosphorus' }, { id: 'potassium', label: 'Potassium' }, 
  { id: 'sulphur', label: 'Sulphur' }, { id: 'magnesium', label: 'Magnesium' },
  { id: 'calcium', label: 'Calcium' }, { id: 'zinc', label: 'Zinc' },
  { id: 'iron', label: 'Iron' }, { id: 'copper', label: 'Copper' },  { id: 'boron', label: 'Boron' }, 
];

const WATER_PARAMS = [
  { id: 'ph', label: 'pH' }, { id: 'ec', label: 'EC' },
  { id: 'nitrate', label: 'Nitrate' }, { id: 'ammonia', label: 'Ammonia' }, 
  { id: 'phosphorus', label: 'Phosphorus' }, { id: 'potassium', label: 'Potassium' }, 
  { id: 'sulphur', label: 'Sulphur' }, { id: 'boron', label: 'Boron' }, 
  { id: 'zinc', label: 'Zinc' }, { id: 'iron', label: 'Iron' }, 
  { id: 'calcium', label: 'Calcium' }, { id: 'magnesium', label: 'Magnesium' },
  { id: 'copper', label: 'Copper' }, { id: 'carbonate', label: 'Carbonate' }, 
  { id: 'bicarbonate', label: 'Bicarbonate' }, { id: 'tds', label: 'TDS' },
];

const BulkUpdateReports = () => {
  const dispatch = useDispatch();
  const role = getRole();

  const [sampleType, setSampleType] = useState('SOIL');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sampleIdsText, setSampleIdsText] = useState('');

  const [paramFilters, setParamFilters] = useState([{ param: 'ph', operator: '=', value: '' }]);
  const [paramLogic, setParamLogic] = useState('AND'); 
  const [valueType, setValueType] = useState('RAW'); 

  const [reports, setReports] = useState([]);
  const [editedReports, setEditedReports] = useState({}); 
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const partnerDropdownRef = useRef(null);

  const [foSearch, setFoSearch] = useState('');
  const [foDropdownOpen, setFoDropdownOpen] = useState(false);
  const foDropdownRef = useRef(null);

  const { fieldOfficers = [] } = useSelector((state) => state.fieldOfficers);
  const partnersList = useSelector((state) => state.partners?.partners ?? []);
  const [displayedFieldOfficers, setDisplayedFieldOfficers] = useState([]);

  const currentParams = sampleType === 'SOIL' ? SOIL_PARAMS : WATER_PARAMS;

  useEffect(() => {
    if (role === 'SUPPORT') {
      dispatch(fetchFieldOfficer({ role: 'SUPPORT', page: 1, limit: 500 }));
      dispatch(fetchPartners({ page: 1, limit: 500 }));
    }
  }, [dispatch, role]);

  useEffect(() => {
    if (!selectedPartner) {
      setDisplayedFieldOfficers(fieldOfficers);
      return;
    }
    axiosInstance.get(`/super-admin/${selectedPartner}/field-officers`)
      .then(({ data }) => setDisplayedFieldOfficers(Array.isArray(data?.fieldOfficers) ? data.fieldOfficers : []))
      .catch(() => setDisplayedFieldOfficers([]));
  }, [selectedPartner, fieldOfficers]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(e.target)) {
        setPartnerDropdownOpen(false);
        setPartnerSearch('');
      }
      if (foDropdownRef.current && !foDropdownRef.current.contains(e.target)) {
        setFoDropdownOpen(false);
        setFoSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSampleTypeChange = (type) => {
    setSampleType(type);
    setReports([]);
    setEditedReports({});
    setParamFilters([{ param: 'ph', operator: '=', value: '' }]);
  };

  const handleFetchReports = async () => {
    setIsLoading(true);
    setReports([]);
    setEditedReports({});
    
    try {
      const payload = {
        sample_type: sampleType,
        partner_id: selectedPartner,
        field_officer_id: selectedFieldOfficer,
        start_date: startDate,
        end_date: endDate,
        sample_ids: sampleIdsText ? sampleIdsText.split(/[\n,]/).map(s => s.trim()).filter(Boolean).join(',') : null,
        param_filters: JSON.stringify(paramFilters.filter(f => f.value !== '')),
        param_logic: paramLogic
      };

      const { data } = await axiosInstance.get('/super-admin/bulk-update/fetch', { params: payload });
      setReports(data.reports || []);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch reports for bulk update.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCellChange = (reportId, field, val) => {
    setEditedReports(prev => ({
      ...prev,
      [reportId]: {
        ...(prev[reportId] || {}),
        [field]: val
      }
    }));
  };

  const handleBulkUpdate = async () => {
    const reportIdsToUpdate = Object.keys(editedReports);
    if (reportIdsToUpdate.length === 0) {
      return Swal.fire('Info', 'No changes detected to save.', 'info');
    }

    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to update ${reportIdsToUpdate.length} ${sampleType} reports.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, update them!'
    });

    if (!confirm.isConfirmed) return;

    setIsUpdating(true);

    let successCount = 0;
    let failCount = 0;

    try {
      for (const id of reportIdsToUpdate) {
        try {
          const originalReport = reports.find(r => r.report_id === id);
          const updates = editedReports[id];
          
          const payload = {
            ...originalReport,
            ...updates,
            sample_type: sampleType,
            value_type: valueType, 
          };

          await axiosInstance.put(`/super-admin/update-sample-report`, payload); 
          successCount++;
        } catch (err) {
          console.error(`Failed to update ${id}`, err);
          failCount++;
        }
      }

      Swal.fire('Complete', `Successfully updated ${successCount} reports. ${failCount > 0 ? `Failed: ${failCount}` : ''}`, successCount > 0 ? 'success' : 'error');
      handleFetchReports(); 
    } finally {
      setIsUpdating(false);
    }
  };

  if (role !== 'SUPPORT') return <Wrapper><h2>Access Denied</h2></Wrapper>;

  const selectedPartnerObj = partnersList.find(
    p => String(p.admin_id) === String(selectedPartner)
  );

  const selectedPartnerLabel = selectedPartner
    ? selectedPartnerObj?.entity_name ||
      selectedPartnerObj?.name ||
      selectedPartner
    : 'All Partners';

  const filteredPartners = partnersList.filter((p) => {
    const label = (p.entity_name || p.name || String(p.admin_id)).toLowerCase();
    return label.includes(partnerSearch.toLowerCase());
  });

  const selectedFoLabel = selectedFieldOfficer
    ? (displayedFieldOfficers.find((fo) => String(fo.field_officer_id) === String(selectedFieldOfficer))?.name || selectedFieldOfficer)
    : 'All Field Officers';

  const filteredFos = displayedFieldOfficers.filter((fo) =>
    (fo.name || '').toLowerCase().includes(foSearch.toLowerCase())
  );

  return (
    <>
      {isUpdating && (
        <div className="fixed inset-0 z-[99999] bg-slate-900/70 flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-emerald-500 mb-4"></div>
          <h2 className="text-white text-2xl font-bold">Processing Bulk Updates...</h2>
          <p className="text-slate-200 mt-2">Please do not refresh or close this page.</p>
        </div>
      )}

      <Wrapper>
        <div className="py-4 w-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-800">Bulk Edit Reports</h2>
            
            {/* SAMPLE TYPE TOGGLE */}
            <div className="flex bg-slate-200 p-1 rounded-lg">
              <button 
                onClick={() => handleSampleTypeChange('SOIL')}
                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${sampleType === 'SOIL' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              >
                SOIL
              </button>
              <button 
                onClick={() => handleSampleTypeChange('WATER')}
                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${sampleType === 'WATER' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              >
                WATER
              </button>
            </div>
          </div>

          {/* CONTROL PANEL */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-4">
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 pb-4 border-b border-slate-100">
              
              <div className="relative w-full" ref={partnerDropdownRef}>
                <button
                  type="button"
                  className="flex items-center justify-between w-full border border-slate-300 rounded p-2 text-sm bg-white text-left focus:border-emerald-500 outline-none"
                  onClick={() => {
                    setPartnerDropdownOpen((o) => !o);
                    setPartnerSearch('');
                  }}
                >
                  <span className="truncate flex-1">{selectedPartnerLabel}</span>
                  <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {partnerDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg">
                    <div className="p-2 border-b border-slate-100 relative">
                      <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search partner..."
                        className="pl-8 pr-3 py-1.5 w-full rounded border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        value={partnerSearch}
                        onChange={(e) => setPartnerSearch(e.target.value)}
                      />
                    </div>
                    <ul className="max-h-52 overflow-y-auto py-1">
                      <li
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${!selectedPartner ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                        onClick={() => {
                          setSelectedPartner('');
                          setPartnerDropdownOpen(false);
                          setPartnerSearch('');
                        }}
                      >
                        All Partners
                      </li>
                      {filteredPartners.length > 0 ? (
                        filteredPartners.map(p => (
                          <li
                            key={p.admin_id}
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${String(selectedPartner) === String(p.admin_id) ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                            onClick={() => {
                              setSelectedPartner(p.admin_id);
                              setPartnerDropdownOpen(false);
                              setPartnerSearch('');
                            }}
                          >
                            {p.entity_name || p.name || p.admin_id}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-sm text-slate-400 italic">No partners found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="relative w-full" ref={foDropdownRef}>
                <button
                  type="button"
                  className="flex items-center justify-between w-full border border-slate-300 rounded p-2 text-sm bg-white text-left focus:border-emerald-500 outline-none"
                  onClick={() => {
                    setFoDropdownOpen((o) => !o);
                    setFoSearch('');
                  }}
                >
                  <span className="truncate flex-1">{selectedFoLabel}</span>
                  <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {foDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg">
                    <div className="p-2 border-b border-slate-100 relative">
                      <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search field officer..."
                        className="pl-8 pr-3 py-1.5 w-full rounded border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        value={foSearch}
                        onChange={(e) => setFoSearch(e.target.value)}
                      />
                    </div>
                    <ul className="max-h-52 overflow-y-auto py-1">
                      <li
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${!selectedFieldOfficer ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                        onClick={() => {
                          setSelectedFieldOfficer('');
                          setFoDropdownOpen(false);
                          setFoSearch('');
                        }}
                      >
                        All Field Officers
                      </li>
                      {filteredFos.length > 0 ? (
                        filteredFos.map(fo => (
                          <li
                            key={fo.field_officer_id}
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${String(selectedFieldOfficer) === String(fo.field_officer_id) ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                            onClick={() => {
                              setSelectedFieldOfficer(fo.field_officer_id);
                              setFoDropdownOpen(false);
                              setFoSearch('');
                            }}
                          >
                            {fo.name}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-sm text-slate-400 italic">No field officers found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <input type="date" className="border border-slate-300 rounded p-2 text-sm w-full" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <input type="date" className="border border-slate-300 rounded p-2 text-sm w-full" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>

              <input type="text" placeholder="Sample IDs (comma separated)" className="border border-slate-300 rounded p-2 text-sm" value={sampleIdsText} onChange={e => setSampleIdsText(e.target.value)} />
            </div>

            {/* PARAMETER FILTERS */}
            <div className="mb-4">
              <div className="flex items-center gap-4 mb-2">
                <h3 className="font-semibold text-sm text-slate-700">Parameter Rules</h3>
                <select className="bg-slate-100 border border-slate-200 rounded p-1 text-xs font-bold" value={paramLogic} onChange={e => setParamLogic(e.target.value)}>
                  <option value="AND">Match ALL (AND)</option>
                  <option value="OR">Match ANY (OR)</option>
                </select>
              </div>

              {paramFilters.map((filter, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <select className="border border-slate-300 rounded p-1.5 text-sm w-32" value={filter.param} onChange={e => { const newF = [...paramFilters]; newF[index].param = e.target.value; setParamFilters(newF); }}>
                    {currentParams.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  
                  <select className="border border-slate-300 rounded p-1.5 text-sm w-24" value={filter.operator} onChange={e => { const newF = [...paramFilters]; newF[index].operator = e.target.value; setParamFilters(newF); }}>
                    <option value="=">=</option>
                    <option value=">">&gt;</option>
                    <option value=">=">&gt;=</option>
                    <option value="<">&lt;</option>
                    <option value="<=">&lt;=</option>
                  </select>
                  
                  <input type="number" step="any" placeholder="Value" className="border border-slate-300 rounded p-1.5 text-sm w-32" value={filter.value} onChange={e => { const newF = [...paramFilters]; newF[index].value = e.target.value; setParamFilters(newF); }} />
                  
                  {paramFilters.length > 1 && (
                    <button onClick={() => setParamFilters(paramFilters.filter((_, i) => i !== index))} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><FaTrash /></button>
                  )}
                </div>
              ))}
              
              <button onClick={() => setParamFilters([...paramFilters, { param: currentParams[0].id, operator: '=', value: '' }])} className="text-emerald-600 text-sm font-medium flex items-center gap-1 mt-2 hover:underline">
                <FaPlus size={12} /> Add Rule
              </button>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Updating As:</span>
                <select className={`border-2 font-bold rounded p-1.5 text-sm ${sampleType === 'SOIL' ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : 'border-blue-500 text-blue-700 bg-blue-50'}`} value={valueType} onChange={e => setValueType(e.target.value)}>
                  <option value="RAW">RAW Values</option>
                  <option value="FINAL">FINAL Values</option>
                </select>
              </div>
              <button onClick={handleFetchReports} className="bg-slate-800 text-white px-6 py-2 rounded-md font-medium hover:bg-slate-900 transition-colors">
                Fetch Data (Max 25)
              </button>
            </div>
          </div>

          {/* EDITABLE TABLE */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {isLoading ? (
              <div className="p-12 text-center text-slate-500">Loading...</div>
            ) : reports.length === 0 ? (
              <div className="p-12 text-center text-slate-500">No {sampleType} reports found matching your criteria.</div>
            ) : (
              <>
                <div className="overflow-x-auto custom-scrollbar max-h-[60vh]">
                  <table className="min-w-full divide-y divide-slate-200 border-collapse">
                    <thead className="bg-slate-50 sticky top-0 z-20">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 sticky left-0 bg-slate-50 z-30">Sample ID</th>
                        {currentParams.map(p => (
                          <th key={p.id} className="px-3 py-2 text-center text-xs font-semibold text-slate-500 bg-slate-50 border-l border-slate-200">{p.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {reports.map((report) => (
                        <tr key={report.report_id} className={`hover:bg-slate-50 transition-colors ${editedReports[report.report_id] ? 'bg-orange-50/30' : ''}`}>
                          <td className="px-3 py-2 text-sm font-mono sticky left-0 bg-white z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                            {report.sample_id}
                          </td>
                          {currentParams.map(p => {
                            const val = editedReports[report.report_id]?.[p.id] ?? report[p.id] ?? '';
                            return (
                              <td key={p.id} className="p-1 border-l border-slate-100">
                                <input
                                  type="text"
                                  className={`w-full p-1.5 text-center text-sm rounded border ${editedReports[report.report_id]?.[p.id] !== undefined ? 'border-orange-400 bg-orange-50' : 'border-transparent hover:border-slate-300 focus:border-emerald-500 bg-transparent'} outline-none`}
                                  value={val}
                                  onChange={(e) => handleCellChange(report.report_id, p.id, e.target.value)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 flex justify-between items-center sticky bottom-0">
                  <span className="text-sm font-medium text-slate-600">
                    {Object.keys(editedReports).length} row(s) modified
                  </span>
                  <button 
                    onClick={handleBulkUpdate} 
                    disabled={Object.keys(editedReports).length === 0}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white px-6 py-2 rounded-md font-medium transition-colors"
                  >
                    <FaSave /> Save Changes
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Wrapper>
    </>
  );
};

export default BulkUpdateReports;